import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLottery } from "@/hooks/use-lottery-selection";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { Dices, Copy, Check, Trash2, FileDown, Bookmark, Plus, Eraser, Hand } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { usePlanLimits } from "@/hooks/use-plan-limits";
import { CheckScopeDialog } from "@/components/app/CheckScopeDialog";
import { cn } from "@/lib/utils";
import { ticketPrice } from "@/lib/pool-math";
import type { SavedGameScope } from "@/lib/check-scope";

function generateGame(min: number, max: number, count: number): number[] {
  const set = new Set<number>();
  while (set.size < count) {
    set.add(min + Math.floor(Math.random() * (max - min + 1)));
  }
  return [...set].sort((a, b) => a - b);
}

const Gerador = () => {
  const { config, selected } = useLottery();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { canAddSavedGames, plan, refreshUsage } = usePlanLimits();
  const [count, setCount] = useState(config.pick);
  const [qty, setQty] = useState(5);
  const SS_KEY = `lt-gerador-games-${selected}`;
  const [games, setGames] = useState<number[][]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = sessionStorage.getItem(SS_KEY);
      return raw ? (JSON.parse(raw) as number[][]) : [];
    } catch {
      return [];
    }
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (games.length === 0) sessionStorage.removeItem(SS_KEY);
      else sessionStorage.setItem(SS_KEY, JSON.stringify(games));
    } catch {
      /* ignore quota errors */
    }
  }, [games, SS_KEY]);
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [scopeOpen, setScopeOpen] = useState(false);
  const [manual, setManual] = useState<number[]>([]);

  // Reset manual selection when lottery changes
  useEffect(() => {
    setManual([]);
  }, [selected]);

  const toggleManual = (n: number) => {
    setManual((prev) => {
      if (prev.includes(n)) return prev.filter((x) => x !== n);
      if (prev.length >= config.maxPick) {
        toast({ title: "Limite atingido", description: `Máximo ${config.maxPick} dezenas.`, variant: "destructive" });
        return prev;
      }
      return [...prev, n].sort((a, b) => a - b);
    });
  };

  const addManualToList = () => {
    if (manual.length < config.minPick) {
      toast({ title: "Selecione mais dezenas", description: `Mínimo ${config.minPick}.`, variant: "destructive" });
      return;
    }
    setGames((p) => [...p, [...manual]]);
    toast({ title: "Jogo adicionado", description: "Use o botão Salvar para guardar em Meus jogos." });
    setManual([]);
  };

  const generate = () => {
    const c = Math.max(config.minPick, Math.min(config.maxPick, count));
    const q = Math.max(1, Math.min(1000, qty));
    setGames(Array.from({ length: q }, () => generateGame(config.min, config.max, c)));
  };

  const copyOne = (idx: number) => {
    const text = games[idx].map((n) => String(n).padStart(2, "0")).join(" - ");
    navigator.clipboard.writeText(text);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 1500);
  };

  const copyAll = () => {
    const text = games.map((g, i) => `Jogo ${i + 1}: ${g.map((n) => String(n).padStart(2, "0")).join(" - ")}`).join("\n");
    navigator.clipboard.writeText(text);
    toast({ title: "Copiado!", description: `${games.length} jogos copiados para a área de transferência.` });
  };

  const exportPDF = () => {
    if (games.length === 0) return;
    navigate("/app/exportar", { state: { games } });
  };

  const openSaveDialog = () => {
    if (!user) {
      toast({ title: "Faça login", description: "Você precisa estar logado para salvar jogos.", variant: "destructive" });
      return;
    }
    if (games.length === 0) return;
    const check = canAddSavedGames(games.length);
    if (!check.ok) {
      toast({
        title: "Limite de jogos salvos atingido",
        description: `Seu plano (${plan.toUpperCase()}) permite salvar até ${check.remaining} jogo(s) a mais. Faça upgrade para salvar mais.`,
        variant: "destructive",
      });
      return;
    }
    setScopeOpen(true);
  };

  const persistGames = async (scopeInfo: { scope: SavedGameScope; from: number | null; until: number | null; specialScheduleId?: string | null }) => {
    if (!user) return;
    setSaving(true);
    try {
      const { data: lottery } = await supabase.from("lotteries").select("id").eq("slug", selected).maybeSingle();
      if (!lottery) throw new Error("Loteria não encontrada");
      const rows = games.map((g) => ({
        user_id: user.id,
        lottery_id: lottery.id,
        numbers: g,
        cost: ticketPrice(selected, g.length),
        label: `Gerado em ${new Date().toLocaleDateString("pt-BR")}`,
        check_scope: scopeInfo.scope,
        check_from_contest: scopeInfo.from,
        check_until_contest: scopeInfo.until,
        special_schedule_id: scopeInfo.specialScheduleId ?? null,
      }));
      const { error } = await supabase.from("saved_games").insert(rows);
      if (error) throw error;
      toast({ title: "Jogos salvos!", description: `${games.length} jogos guardados na sua conta.` });
      refreshUsage();
    } catch (e: any) {
      toast({ title: "Erro ao salvar", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-3xl text-foreground">Gerador — {config.name}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Gerador aleatório uniforme. Cada número tem a mesma probabilidade.
        </p>
      </div>

      <Card className="border-lt-border bg-card">
        <CardHeader>
          <CardTitle className="font-serif text-xl">Configuração</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <Label className="text-xs text-muted-foreground">Números por jogo</Label>
              <Input
                type="number"
                min={config.minPick}
                max={config.maxPick}
                value={count}
                onChange={(e) => setCount(Number(e.target.value))}
                className="mt-1 border-lt-border bg-background"
              />
              <p className="mt-1 text-[10px] text-muted-foreground">mínimo {config.minPick}, máximo {config.maxPick} (regra da Caixa)</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Quantidade de jogos</Label>
              <Input
                type="number"
                min={1}
                max={1000}
                value={qty}
                onChange={(e) => setQty(Number(e.target.value))}
                className="mt-1 border-lt-border bg-background"
              />
              <p className="mt-1 text-[10px] text-muted-foreground">de 1 a 1000</p>
            </div>
            <div className="flex items-end">
              <Button onClick={generate} className="w-full bg-primary text-primary-foreground hover:opacity-90">
                <Dices className="mr-2 h-4 w-4" /> Gerar jogos
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-lt-border bg-card">
        <CardHeader className="flex-row items-center justify-between space-y-0 flex-wrap gap-2">
          <div>
            <CardTitle className="font-serif text-xl flex items-center gap-2">
              <Hand className="h-4 w-4 text-primary" /> Monte seu próprio jogo
            </CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              Clique nas dezenas para escolher entre {config.minPick} e {config.maxPick}. Selecionadas: <span className="text-foreground">{manual.length}</span>
            </p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => setManual([])} disabled={manual.length === 0}>
              <Eraser className="mr-1 h-3.5 w-3.5" /> Limpar
            </Button>
            <Button
              size="sm"
              onClick={addManualToList}
              disabled={manual.length < config.minPick}
              className="bg-primary text-primary-foreground hover:opacity-90"
            >
              <Plus className="mr-1 h-3.5 w-3.5" /> Adicionar à lista
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-10 gap-1.5 sm:grid-cols-12 md:grid-cols-15 lg:grid-cols-20">
            {Array.from({ length: config.max - config.min + 1 }, (_, i) => config.min + i).map((n) => {
              const isOn = manual.includes(n);
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => toggleManual(n)}
                  className={cn(
                    "relative flex h-9 items-center justify-center rounded-md border text-[12px] font-medium transition-all",
                    isOn
                      ? "border-primary bg-primary text-primary-foreground shadow"
                      : "border-lt-border bg-lt-bg2 hover:border-primary/50 hover:bg-primary/5",
                  )}
                >
                  {String(n).padStart(2, "0")}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {games.length > 0 && (
        <Card className="border-lt-border bg-card">
          <CardHeader className="flex-row items-center justify-between space-y-0 flex-wrap gap-2">
            <CardTitle className="font-serif text-xl">Jogos gerados ({games.length})</CardTitle>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={copyAll}>
                <Copy className="mr-1 h-3.5 w-3.5" /> Copiar
              </Button>
              <Button size="sm" variant="outline" onClick={openSaveDialog} disabled={saving || !user}>
                <Bookmark className="mr-1 h-3.5 w-3.5" /> {saving ? "Salvando..." : "Salvar"}
              </Button>
              <Button size="sm" className="bg-primary text-primary-foreground hover:opacity-90" onClick={exportPDF}>
                <FileDown className="mr-1 h-3.5 w-3.5" /> Exportar PDF
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setGames([])}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {games.map((g, i) => (
                <div key={i} className="flex items-center justify-between gap-3 rounded-md border border-lt-border bg-background/40 px-3 py-2">
                  <div className="flex items-center gap-3">
                    <span className="w-10 font-mono text-xs text-muted-foreground">#{String(i + 1).padStart(2, "0")}</span>
                    <div className="flex flex-wrap gap-1.5">
                      {g.map((n) => (
                        <span key={n} className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-lt-gold-border bg-lt-gold-bg font-mono text-xs text-foreground">
                          {String(n).padStart(2, "0")}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => copyOne(i)}>
                      {copiedIdx === i ? <Check className="h-4 w-4 text-accent" /> : <Copy className="h-3.5 w-3.5" />}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      <CheckScopeDialog
        open={scopeOpen}
        onOpenChange={setScopeOpen}
        lotterySlug={selected}
        lotteryName={config.name}
        onConfirm={async ({ scope, from, until, specialScheduleId }) => {
          await persistGames({ scope, from, until, specialScheduleId });
        }}
      />
    </div>
  );
};

export default Gerador;
